import { useState } from 'react';
import axios from 'axios';
import { Phone, Mail, MapPin, MessageCircle, Loader2, Check } from 'lucide-react';
import { BUSINESS } from '../data/content';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault(); setSubmitting(true); setError('');
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
    } catch (err) { setError(err?.response?.data?.detail || 'Could not send. Please call us instead.'); }
    finally { setSubmitting(false); }
  };

  return (
    <>
      <SEO title="Contact Laraé Logistics — Removals London" description="Get in touch with Laraé Logistics for removals, man and van or office relocations across London. Call 020 3857 7761." />
      <section className="bg-forest text-white py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
          <p className="font-manrope text-sm text-oat uppercase tracking-widest mb-3">Contact</p>
          <h1 className="font-outfit text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight">Talk to us.</h1>
        </div>
      </section>
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12 grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-2 space-y-5">
            <Info icon={Phone} title="Phone" lines={[BUSINESS.phone, `Mobile: ${BUSINESS.mobile}`]} href={`tel:${BUSINESS.phoneRaw}`} />
            <Info icon={MessageCircle} title="WhatsApp" lines={[BUSINESS.mobile]} href={`https://wa.me/${BUSINESS.whatsappRaw}`} />
            <Info icon={Mail} title="Email" lines={[BUSINESS.email]} href={`mailto:${BUSINESS.email}`} />
            <Info icon={MapPin} title="Service area" lines={[BUSINESS.address, BUSINESS.hours]} />
          </div>
          <div className="lg:col-span-3">
            {done ? (
              <div className="bg-white border border-sand rounded-2xl p-10 text-center">
                <div className="w-14 h-14 rounded-full bg-forest grid place-items-center mx-auto mb-4"><Check size={24} className="text-white" /></div>
                <h3 className="font-outfit text-2xl font-semibold text-ink">Message sent</h3>
                <p className="font-manrope text-sm text-ink/65 mt-2">We'll get back to you shortly.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="bg-white border border-sand rounded-2xl p-7 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField label="Your name"><input data-testid="contact-name" required value={form.name} onChange={e=>update('name', e.target.value)} className={inputCls} /></FormField>
                  <FormField label="Email"><input data-testid="contact-email" type="email" required value={form.email} onChange={e=>update('email', e.target.value)} className={inputCls} /></FormField>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField label="Phone"><input value={form.phone} onChange={e=>update('phone', e.target.value)} className={inputCls} /></FormField>
                  <FormField label="Subject"><input value={form.subject} onChange={e=>update('subject', e.target.value)} className={inputCls} /></FormField>
                </div>
                <FormField label="Message"><textarea required rows={5} value={form.message} onChange={e=>update('message', e.target.value)} className={inputCls + ' resize-none'} /></FormField>
                {error && <p className="text-sm text-red-700">{error}</p>}
                <button data-testid="contact-submit" disabled={submitting} className="bg-forest text-white rounded-full px-7 py-3.5 font-medium inline-flex items-center gap-2 disabled:opacity-50">
                  {submitting && <Loader2 size={16} className="animate-spin" />} Send message
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
}

const inputCls = 'w-full px-4 py-3 rounded-xl border border-sand bg-white text-ink focus:border-forest focus:outline-none focus:ring-2 focus:ring-forest/20 text-sm';
function FormField({ label, children }) {
  return <div><label className="font-manrope text-sm font-medium text-ink mb-2 block">{label}</label>{children}</div>;
}
function Info({ icon: Icon, title, lines, href }) {
  const content = (
    <div className="bg-white border border-sand rounded-2xl p-6 flex items-start gap-4 hover:border-forest transition-colors">
      <div className="w-11 h-11 rounded-xl bg-sand grid place-items-center text-forest shrink-0"><Icon size={20} /></div>
      <div>
        <div className="font-outfit text-base font-semibold text-ink">{title}</div>
        {lines.map((l, i) => <div key={i} className="font-manrope text-sm text-ink/65 mt-0.5">{l}</div>)}
      </div>
    </div>
  );
  return href ? <a href={href}>{content}</a> : content;
}
